import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import Notice from '../../components/Notice.jsx';
import RoleChips from '../../components/RoleChips.jsx';
import { api } from '../../lib/api.js';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';
import { customerSectionPath } from '../../lib/navigation.js';
import { OVERRIDES_OWN_UNSUBSCRIBE, buildRecipientRows, cannotClearText, recipientLabel, recipientSearchText } from '../../lib/recipient-rows.js';
import { SCOPE_LABEL, formatLocalWhen } from '../../lib/watch-format.js';

const TONE = { green: 'badge-green', amber: 'badge-amber', red: 'badge-red', slate: 'badge-slate' };
const SOURCE_NAME = { daily: 'Daily collection', live: 'Live watch' };
const scopeName = (s) => (s.classicList ? 'Daily email unsubscribe' : (SCOPE_LABEL[s.scope] ?? s.scope));

export default function NotificationRecipients({ customerId, data, config, operatorAccount, onChanged, onConfigSaved }) {
  const navigate = useNavigate();
  const [clearing, setClearing] = useState(null); // { row, suppression }
  const [editingDefault, setEditingDefault] = useState(null); // null closed, else the address text
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [dialogError, setDialogError] = useState(null);
  useUnsavedChanges(editingDefault !== null && editingDefault.trim() !== (config?.alertEmail ?? '').trim());
  const focusRef = useRef(null);
  const tableWrapRef = useRef(null);
  const rowFocusRef = useRef(null);
  const addRef = useRef(null);
  const closeDialogs = () => { setClearing(null); setEditingDefault(null); setDialogError(null); };

  // Focus returns to the row's "Actions for <row>" button, else the table region.
  function markRowFocus(title) {
    const wrap = tableWrapRef.current;
    const btn = wrap ? [...wrap.querySelectorAll('button[aria-label]')].find((b) => b.getAttribute('aria-label') === `Actions for ${title}`) : null;
    rowFocusRef.current = btn ?? wrap?.querySelector('[role="region"][tabindex]') ?? focusRef.current;
  }
  function startClear(row, suppression) { setNotice(null); setDialogError(null); markRowFocus(String(recipientLabel(row))); setClearing({ row, suppression }); }
  function startEditDefault(text, title) {
    setNotice(null); setDialogError(null);
    if (title) markRowFocus(title); else rowFocusRef.current = addRef.current ?? focusRef.current;
    setEditingDefault(text);
  }
  // A reload failure happens after the dialog closed, so it is a page notice, never a dialog error.
  async function reload(...fns) {
    try {
      for (const fn of fns) await fn?.();
    } catch (e) {
      setNotice({ tone: 'error', text: e?.message ?? 'Saved, but the list could not be refreshed. Reload to try again.' });
    }
  }

  const classic = data.classic;
  const ruleLabel = (id) => classic?.catalog?.find((e) => e.id === id)?.label ?? id;
  const rows = useMemo(
    () => buildRecipientRows({
      rules: classic?.rules ?? [],
      ruleLabel,
      guardians: operatorAccount ? null : data.guardians?.guardians ?? null,
      classicSuppressions: data.suppressions?.suppressions ?? [],
      watchSuppressions: operatorAccount ? null : data.watchSuppressions?.items ?? null,
      alertEmail: config?.alertEmail ?? '',
    }),
    [classic, data.guardians, data.suppressions, data.watchSuppressions, config?.alertEmail, operatorAccount], // eslint-disable-line react-hooks/exhaustive-deps
  );

  async function confirmClear() {
    if (busy || !clearing) return;
    const { suppression: s } = clearing;
    const address = s.address ?? clearing.row.address;
    setBusy(true);
    setDialogError(null);
    try {
      if (s.classicList) await api.deleteSuppression(customerId, address);
      else await api.clearWatchSuppression(customerId, s.scope, address);
    } catch (e) {
      setDialogError(e?.message ?? 'Could not re-enable the address.');
      setBusy(false);
      return;
    }
    setNotice({ tone: 'ok', text: `Re-enabled ${address}.` });
    closeDialogs();
    setBusy(false);
    await reload(onChanged);
  }

  async function saveDefault() {
    if (busy || editingDefault === null) return;
    setBusy(true);
    setDialogError(null);
    try {
      await api.updateConfig(customerId, { alertEmail: editingDefault.trim() || null });
    } catch (e) {
      setDialogError(e?.message ?? 'Could not save the address.');
      setBusy(false);
      return;
    }
    closeDialogs();
    setNotice({ tone: 'ok', text: 'Default alert address saved.' });
    setBusy(false);
    await reload(onConfigSaved, onChanged);
  }

  const columns = [
    {
      id: 'recipient',
      header: 'Recipient',
      sortValue: (r) => String(recipientLabel(r)).toLowerCase(),
      cell: (r) => (
        <div className="min-w-0 break-words">
          {r.name && r.address ? <div className="font-medium text-slate-900">{r.name}</div> : null}
          <div className={r.name && r.address ? 'text-xs text-slate-600' : 'text-slate-900'}>{r.address ?? r.name}</div>
        </div>
      ),
    },
    { id: 'receives', header: 'Receives', cell: (r) => <RoleChips roles={r.receives} max={2} label={`What ${recipientLabel(r)} receives`} /> },
    { id: 'source', header: 'Source', hideBelow: 'md', cell: (r) => r.sources.map((s) => SOURCE_NAME[s]).join(', ') },
    { id: 'status', header: 'Status', sortValue: (r) => r.status.label, cell: (r) => <span className={TONE[r.status.tone]}>{r.status.label}</span> },
  ];

  const filters = [
    { id: 'source', label: 'Source', options: [{ value: 'daily', label: 'Daily collection' }, { value: 'live', label: 'Live watch' }], test: (r, v) => r.sources.includes(v) },
    { id: 'status', label: 'Status', options: [{ value: 'active', label: 'Active' }, { value: 'attention', label: 'Needs attention' }, { value: 'unsubscribed', label: 'Unsubscribed' }], test: (r, v) => r.status.key === v || (v === 'attention' && r.status.key === 'checking') },
  ];

  function rowActions(r) {
    return [
      { id: 'people', label: 'Manage in People', hidden: !r.guardian || operatorAccount, onSelect: () => navigate(customerSectionPath(customerId, 'people')) },
      { id: 'default', label: 'Edit default alert address', hidden: !r.isDefault, onSelect: () => startEditDefault(r.address ?? '', String(recipientLabel(r))) },
      ...r.suppressions.map((s, i) => ({
        id: `clear-${i}`,
        label: `Re-enable (${scopeName(s)})`,
        disabled: !s.clearable,
        disabledReason: s.clearable ? undefined : cannotClearText(s.scope),
        onSelect: () => startClear(r, s),
      })),
    ];
  }

  if (data.errors.classic) return <div className="card border-red-200 text-red-700" role="alert">{data.errors.classic}</div>;

  return (
    <div className="space-y-3 focus:outline-none" ref={focusRef} tabIndex={-1}>
      {notice ? <p role={notice.tone === 'error' ? 'alert' : 'status'} className={`rounded px-3 py-2 text-sm ${notice.tone === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'}`}>{notice.text}</p> : null}
      {data.errors.guardians || data.errors.watchSuppressions ? <Notice tone="warn" role="status" titleAs="p" title="Some recipients could not be loaded">Live watch recipients are missing from this list. Reload to try again.</Notice> : null}
      <div ref={tableWrapRef}>
      <ConsoleTable
        label="Recipients"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        rowName={(r) => String(recipientLabel(r))}
        searchText={recipientSearchText}
        searchPlaceholder="Search recipients"
        filters={filters}
        renderExpanded={(r) => (
          <div className="space-y-2 text-sm">
            <p><span className="text-slate-600">Receives:</span> {r.receives.map((c) => c.label).join(', ') || 'Nothing yet'}</p>
            {r.suppressions.map((s, i) => <p key={i}><span className="text-slate-600">{scopeName(s)}:</span> {s.at ? `since ${formatLocalWhen(s.at)}` : 'date unknown'}</p>)}
          </div>
        )}
        rowActions={rowActions}
        actions={operatorAccount && config?.alertEmail ? null : (
          <>
            {config?.alertEmail ? null : <button ref={addRef} type="button" className="btn-secondary" onClick={() => startEditDefault('', null)}>Add default alert address</button>}
            {operatorAccount ? null : <Link className="btn-secondary" to={customerSectionPath(customerId, 'people')}>Manage guardians in People</Link>}
          </>
        )}
        emptyState={{ title: 'No recipients yet', body: 'Recipients appear here once a daily rule names one or a guardian is added in People.' }}
      />
      </div>
      {clearing ? (
        <Dialog
          open
          title="Re-enable address"
          onClose={() => { if (!busy) closeDialogs(); }}
          returnFocusRef={rowFocusRef}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={closeDialogs} disabled={busy}>Cancel</button>
              <button type="button" className="btn-primary" onClick={confirmClear} disabled={busy} aria-busy={busy}>Confirm</button>
            </>
          )}
        >
          <p><span className="break-all">Clear {clearing.row.address}?</span> Mail to this address will resume.{OVERRIDES_OWN_UNSUBSCRIBE.has(clearing.suppression.scope) && !clearing.suppression.classicList ? " This overrides the recipient's own unsubscribe." : ''}</p>
          <DialogError message={dialogError} />
        </Dialog>
      ) : null}
      {editingDefault !== null ? (
        <Dialog
          open
          title="Edit default alert address"
          onClose={() => { if (!busy) closeDialogs(); }}
          returnFocusRef={rowFocusRef}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={closeDialogs} disabled={busy}>Cancel</button>
              <button type="button" className="btn-primary" onClick={saveDefault} disabled={busy} aria-busy={busy}>Save</button>
            </>
          )}
        >
          <label className="block text-sm">
            <span className="label">Default alert address</span>
            <input type="email" className="input" value={editingDefault} onChange={(e) => setEditingDefault(e.target.value)} placeholder="alerts@customer-domain.com" />
          </label>
          <p className="text-xs text-slate-600">Daily rules that name the customer default send here. Leave empty to clear it.</p>
          <DialogError message={dialogError} />
        </Dialog>
      ) : null}
    </div>
  );
}
